import {
  RDO,
  MaterialEntry, MaterialConformity,
  ActivityEntry, ActivityPhoto,
  PhotoEntry,
  VideoEntry,
  AttachmentEntry,
  EquipmentEntry,
  OccurrenceEntry, OccurrencePhoto,
  CommentEntry,
  WeatherInfo,
} from "./schema";
import type { LegacyRDO } from "./legacyTypes";

// ============================================================================
// Helpers
// ============================================================================
const num = (v: any): number => (typeof v === "number" ? v : Number(v ?? 0) || 0);
const bool = (v: any): boolean =>
  typeof v === "boolean" ? v : String(v).toLowerCase() === "true";

/** Primeiro valor não nulo/indefinido de uma lista. */
const first = <T>(...vals: T[]): T | undefined =>
  vals.find((v) => v !== undefined && v !== null);

/** Converte Date -> ISO string; mantém string como está. */
const dstr = (v: any): string | undefined => {
  if (v == null) return undefined;
  if (v instanceof Date) return v.toISOString();
  return v as string;
};

// ============================================================================
// LEGADO -> RDO (unificado)
//
// Lê o formato REAL usado pelas páginas/dialogs/storage (chaves em inglês,
// materiais em { received, used }, equipamentos em `equipment`, comentários
// como lista plana com parentId) e também aceita aliases PT legados.
// ============================================================================
export function normalizeFromRDOPAGE(legacy: LegacyRDO | any): RDO {
  legacy = legacy ?? {};

  // ----- Mão de obra: passthrough (o módulo reutiliza os componentes legados)
  const workforce = Array.isArray(legacy?.workforce) ? legacy.workforce : [];

  // ----- Clima: passthrough
  const weather: WeatherInfo | undefined =
    legacy?.weather && typeof legacy.weather === "object" ? legacy.weather : undefined;

  // ----- Materiais: aceita array OU objeto { received, used } -----
  const rawMaterials = legacy?.materials ?? legacy?.materiais ?? [];
  const flatMaterials: any[] = Array.isArray(rawMaterials)
    ? rawMaterials
    : rawMaterials && typeof rawMaterials === "object"
      ? [
          ...(Array.isArray(rawMaterials.received)
            ? rawMaterials.received.map((m: any) => ({ ...m, kind: m?.kind ?? "received" }))
            : []),
          ...(Array.isArray(rawMaterials.used)
            ? rawMaterials.used.map((m: any) => ({ ...m, kind: m?.kind ?? "used" }))
            : []),
        ]
      : [];

  const materials: MaterialEntry[] = flatMaterials.map((m: any, index: number): MaterialEntry => {
    const rawStatus = m.status as string | undefined;
    const status: MaterialConformity | undefined =
      rawStatus === "conforme" || rawStatus === "aguardando_inspecao" || rawStatus === "recusado"
        ? rawStatus
        : undefined;
    const conformity: "ok" | "nok" | undefined =
      m.conformity ??
      (status === "conforme"
        ? "ok"
        : status === "recusado"
          ? "nok"
          : rawStatus === "Conforme"
            ? "ok"
            : rawStatus === "Não conforme"
              ? "nok"
              : undefined);
    const kind =
      m.kind ??
      (m.tipo === "Recebido" ? "received" : m.tipo === "Utilizado" ? "used" : m.materialType ?? "used");

    return {
      // Materiais de payloads legados não têm id. Sem id, o React perde a key
      // e — pior — handleDeleteMaterial(undefined) apagava TODOS os materiais
      // sem id de uma vez. Sintetiza um id determinístico (vira canônico ao
      // salvar, pois o mapeamento reverso persiste o id).
      id: m.id ?? `${kind}-${index}`,
      kind,
      materialId: first(m.materialId, m.idMaterial),
      name: first(m.material, m.name, m.nome) ?? "",
      unit: first(m.unit, m.unidade) ?? "",
      qty: num(first(m.quantity, m.qty, m.quantidade)),
      supplierName: first(m.supplier, m.supplierName, m.fornecedor),
      document: first(m.document, m.documento),
      receivedAt: first(m.time, m.receivedAt, m.horarioRecebimento),
      conformity,
      status,
      location: m.location,
      notes: first(m.observations, m.observation, m.notes, m.observacoes),
    };
  });

  // ----- Atividades -----
  const activities: ActivityEntry[] = (legacy?.activities ?? legacy?.atividades ?? []).map(
    (a: any): ActivityEntry => ({
      id: a.id ?? undefined,
      title: first(a.activity, a.title, a.nome, a.descricao) ?? "",
      location: first(a.location, a.local),
      progress: num(first(a.completion, a.progress, a.percentual, a.progresso)),
      responsible: first(a.responsible, a.responsavel),
      notes: first(a.description, a.notes, a.observacoes),
      start: a.start,
      end: a.end,
      linkedTask: a.linkedTask,
      photos: (a.photos ?? a.fotos ?? []).map((p: any): ActivityPhoto => ({
        id: p.id ?? undefined,
        url: first(p.url, p.link) ?? "",
        caption: first(p.caption, p.legenda) ?? "",
        hiddenFromReport: !!(p.hiddenFromReport ?? p.ocultarNoRelatorio),
        createdAt: first(p.timestamp, p.createdAt, p.data),
      })),
    })
  );

  // ----- Fotos (container geral) -----
  const photos: PhotoEntry[] = (legacy?.photos ?? legacy?.fotos ?? []).map(
    (p: any): PhotoEntry => ({
      id: p.id ?? undefined,
      url: first(p.url, p.link) ?? "",
      caption: first(p.caption, p.legenda) ?? "",
      source: first(p.source, p.origem) ?? "general",
      originId: first(p.originId, p.idOrigem),
      hiddenFromReport: !!(p.hiddenFromReport ?? p.ocultarNoRelatorio),
      createdAt: first(p.timestamp, p.createdAt, p.data),
      uploadedBy: first(p.uploadedBy, p.autor),
      thumbnailUrl: p.thumbnailUrl,
      activityIndex: p.activityIndex,
      occurrenceIndex: p.occurrenceIndex,
      activityId: p.activityId,
    })
  );

  // ----- Vídeos -----
  const videos: VideoEntry[] = (legacy?.videos ?? legacy?.videosContainer ?? []).map(
    (v: any): VideoEntry => ({
      id: v.id ?? undefined,
      url: first(v.url, v.link) ?? "",
      caption: first(v.caption, v.title, v.legenda) ?? "",
      title: v.title,
      thumbUrl: first(v.thumbnail, v.thumbUrl),
      createdAt: first(v.timestamp, v.createdAt, v.data),
      duration: v.duration,
      uploadedBy: first(v.uploadedBy, v.autor),
    })
  );

  // ----- Anexos -----
  const attachments: AttachmentEntry[] = (legacy?.attachments ?? legacy?.anexos ?? []).map(
    (a: any): AttachmentEntry => ({
      id: a.id ?? undefined,
      name: first(a.name, a.nome) ?? "",
      mime: first(a.type, a.mime, a.tipo) ?? "application/octet-stream",
      type: a.type,
      size: a.size ?? a.tamanho,
      url: first(a.url, a.link),
      uploadedBy: first(a.uploadedBy, a.autor),
      createdAt: first(a.timestamp, a.createdAt, a.data),
      permissions: a.permissions,
      previewable: a.previewable,
      versions: a.versions,
    })
  );

  // ----- Equipamentos (legado: `equipment` singular, nome em `type`) -----
  const equipments: EquipmentEntry[] = (
    legacy?.equipment ?? legacy?.equipments ?? legacy?.equipamentos ?? []
  ).map((e: any): EquipmentEntry => ({
    id: e.id ?? undefined,
    name: first(e.type, e.name, e.nome) ?? "",
    qty: num(first(e.quantity, e.qty, e.quantidade)),
    rented:
      typeof e.rented === "boolean"
        ? e.rented
        : e.ownershipType === "locado" || bool(e.locado),
    rentDueDate: dstr(first(e.leaseExpiryDate, e.rentDueDate, e.vencimentoLocacao)),
    identification: e.identification,
    operator: e.operator,
    condition: e.condition,
    ownershipType: e.ownershipType,
    notes: first(e.notes, e.observations, e.observacoes),
  }));

  // ----- Ocorrências -----
  const occurrences: OccurrenceEntry[] = (legacy?.occurrences ?? legacy?.ocorrencias ?? []).map(
    (o: any): OccurrenceEntry => ({
      id: o.id ?? undefined,
      title: first(o.title, o.titulo, o.type) ?? "",
      type: o.type,
      description: first(o.description, o.descricao),
      impact: o.impact,
      actionTaken: o.actionTaken,
      responsible: first(o.responsible, o.responsavel),
      location: first(o.location, o.local),
      severity: first(o.severity, o.gravidade),
      hidePhotos: o.hidePhotos,
      notes: first(o.notes, o.observacoes),
      photos: (o.photos ?? o.fotos ?? []).map((p: any): OccurrencePhoto => ({
        id: p.id ?? undefined,
        url: first(p.url, p.link) ?? "",
        caption: first(p.caption, p.legenda) ?? "",
        hiddenFromReport: !!(p.hiddenFromReport ?? p.ocultarNoRelatorio),
        createdAt: first(p.timestamp, p.createdAt, p.data),
        uploadedBy: first(p.uploadedBy, p.autor),
      })),
    })
  );

  // ----- Comentários (lista PLANA com parentId; aceita aliases PT/aninhados) -----
  const mapComment = (c: any): CommentEntry => ({
    id: String(c.id ?? c.commentId ?? crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)),
    authorId: String(first(c.user?.id, c.authorId, c.autorId) ?? ""),
    authorName: first(c.user?.name, c.authorName, c.autorNome) ?? "Usuário",
    role: first(c.user?.role, c.role),
    avatarUrl: first(c.user?.avatar, c.avatarUrl, c.fotoAutor),
    createdAt: first(c.datetime, c.createdAt, c.criadoEm) ?? new Date().toISOString(),
    updatedAt: first(c.updatedAt, c.atualizadoEm),
    text: first(c.text, c.mensagem) ?? "",
    canDelete: c.canEdit ?? c.canDelete ?? c.podeExcluir,
    canReply: c.canReply ?? c.podeResponder ?? true,
    parentId: c.parentId,
    replies: Array.isArray(c.replies ?? c.respostas)
      ? (c.replies ?? c.respostas).map(mapComment)
      : [],
  });
  const comments: CommentEntry[] = (legacy?.comments ?? legacy?.comentarios ?? []).map(mapComment);

  return {
    id: legacy?.id ?? undefined,
    obraId: legacy?.obraId ?? "",
    date: legacy?.date ?? "",
    status: (legacy?.status ?? "draft") as RDO["status"],
    weather,
    workforce,
    materials,
    activities,
    photos,
    videos,
    attachments,
    equipments,
    occurrences,
    comments,
  };
}

// ============================================================================
// RDO (unificado) -> LEGADO (formato REAL persistido)
// ============================================================================
export function toRDOPAGEPayload(rdo: RDO): LegacyRDO {
  // Materiais -> { received, used }
  const received: any[] = [];
  const used: any[] = [];
  (rdo.materials ?? []).forEach((m) => {
    const common: any = {
      id: m.id,
      material: m.name,
      unit: m.unit,
      quantity: m.qty,
      observation: m.notes,
      observations: m.notes,
    };
    if (m.kind === "received") {
      received.push({
        ...common,
        supplier: m.supplierName,
        document: m.document,
        time: m.receivedAt,
        status:
          m.status ??
          (m.conformity === "ok" ? "conforme" : m.conformity === "nok" ? "recusado" : undefined),
      });
    } else {
      used.push({ ...common, location: m.location });
    }
  });
  const materials = { received, used };

  const activities = (rdo.activities ?? []).map((a) => ({
    id: a.id,
    activity: a.title,
    location: a.location,
    start: a.start,
    end: a.end,
    completion: a.progress,
    responsible: a.responsible,
    description: a.notes,
    linkedTask: a.linkedTask,
    photos: (a.photos ?? []).map((p) => ({
      id: p.id,
      url: p.url,
      caption: p.caption,
      hiddenFromReport: !!p.hiddenFromReport,
      timestamp: p.createdAt,
    })),
  }));

  const photos = (rdo.photos ?? []).map((p) => ({
    id: p.id,
    url: p.url,
    caption: p.caption,
    source: p.source,
    hiddenFromReport: !!p.hiddenFromReport,
    timestamp: p.createdAt,
    thumbnailUrl: p.thumbnailUrl,
    activityIndex: p.activityIndex,
    occurrenceIndex: p.occurrenceIndex,
    activityId: p.activityId,
    uploadedBy: p.uploadedBy,
  }));

  const videos = (rdo.videos ?? []).map((v) => ({
    id: v.id,
    url: v.url,
    thumbnail: v.thumbUrl,
    title: first(v.title, v.caption),
    caption: v.caption,
    timestamp: v.createdAt,
    duration: v.duration,
    uploadedBy: v.uploadedBy,
  }));

  const attachments = (rdo.attachments ?? []).map((a) => ({
    id: a.id,
    name: a.name,
    type: first(a.type, a.mime),
    size: a.size,
    url: a.url,
    timestamp: a.createdAt,
    uploadedBy: a.uploadedBy,
    permissions: a.permissions,
    previewable: a.previewable,
    versions: a.versions,
  }));

  // Equipamentos -> `equipment` (singular), nome em `type`
  const equipment = (rdo.equipments ?? []).map((e) => ({
    id: e.id,
    type: e.name,
    identification: e.identification,
    condition: e.condition,
    operator: e.operator,
    quantity: e.qty,
    ownershipType: e.ownershipType ?? (e.rented ? "locado" : "proprio"),
    leaseExpiryDate: e.rentDueDate,
    observations: e.notes,
  }));

  const occurrences = (rdo.occurrences ?? []).map((o) => ({
    id: o.id,
    type: first(o.type, o.title),
    description: o.description,
    impact: o.impact,
    actionTaken: o.actionTaken,
    responsible: o.responsible,
    location: o.location,
    hidePhotos: o.hidePhotos,
    photos: (o.photos ?? []).map((p) => ({
      id: p.id,
      url: p.url,
      caption: p.caption,
      hiddenFromReport: !!p.hiddenFromReport,
      timestamp: p.createdAt,
      uploadedBy: p.uploadedBy,
    })),
  }));

  // Comentários -> lista PLANA com user{}/datetime/parentId
  const comments = (rdo.comments ?? []).map((c) => ({
    id: c.id,
    user: {
      id: c.authorId,
      name: c.authorName,
      role: c.role,
      avatar: c.avatarUrl,
    },
    datetime: c.createdAt,
    text: c.text,
    canEdit: c.canDelete,
    parentId: c.parentId,
  }));

  return {
    id: rdo.id,
    obraId: rdo.obraId,
    date: rdo.date,
    status: rdo.status,
    weather: rdo.weather,
    workforce: rdo.workforce as any, // passthrough
    materials,
    activities,
    photos,
    videos,
    attachments,
    equipment,
    occurrences,
    comments,
  };
}

// ---------- PREENCHENDO <-> RDO (aliases) ----------
export function normalizeFromPreenchendo(preenchendo: any): RDO {
  return normalizeFromRDOPAGE(preenchendo);
}

export function toPreenchendoPayload(rdo: RDO): LegacyRDO {
  return toRDOPAGEPayload(rdo);
}
